import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Badge, Card, EmptyState, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/formatters';

interface EventRow {
  id: string;
  type: string;
  title: string;
  startTime: string;
  status: string;
  maxSeats: number | null;
  _count?: { registrations: number };
}

export function EventsPage() {
  const { data, isLoading } = useQuery<{ items: EventRow[] }>({
    queryKey: ['events'],
    queryFn: async () => (await api.get('/admin/events', { params: { pageSize: 100 } })).data,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl">Events</h1>
        <Link
          to="/admin/events/new"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-navy-900 text-cream-100 text-sm font-medium hover:bg-navy-800"
        >
          New event
        </Link>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (data?.items ?? []).length === 0 ? (
        <EmptyState title="No events" description="Create your first lesson or game night." />
      ) : (
        <Card>
          <ul className="divide-y divide-cream-300">
            {data!.items.map((e) => (
              <li key={e.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <Link to={`/admin/events/${e.id}`} className="font-medium hover:underline">
                    {e.title}
                  </Link>
                  <div className="text-sm text-ink-muted">{formatDateTime(e.startTime)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge>{e.type}</Badge>
                  <Badge variant={e.status === 'PUBLISHED' ? 'success' : 'default'}>{e.status}</Badge>
                  {e.maxSeats && (
                    <span className="text-sm text-ink-muted">
                      {e._count?.registrations ?? 0} / {e.maxSeats}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* TODO(section-4): month-view React Big Calendar, filters (type/status/date range), recurring-event modal */}
    </div>
  );
}
